// Entry point for the docs-site samples bundle. Each import + register
// call here makes one `<ppp-sample-*>` custom element available to the
// Blazor demo host once `samples.js` has been loaded by App.razor.
//
// Adding a new sample is a two-step change: write the .tsx file under
// samples/<area>/, then add a register call below. The .tsx itself stays
// clean (no provider envelope, no registration) so the source tab on the
// docs page shows exactly what a consumer would paste into their own app.

import { defineSampleElement } from './define-sample-element';
import { setMapLibreWorkerUrl } from '@powerportalspro/react-maps';
import { MapMainSample } from './maps/MapMainSample';
import { MapRecordsSample } from './maps/MapRecordsSample';
import { BoolEditMainSample } from './editors/BoolEditMainSample';
import { BoolEditPropertiesSample } from './editors/BoolEditPropertiesSample';
import { ChoiceEditMainSample } from './editors/ChoiceEditMainSample';
import { ChoiceEditPropertiesSample } from './editors/ChoiceEditPropertiesSample';
import { DateEditMainSample } from './editors/DateEditMainSample';
import { DateEditPropertiesSample } from './editors/DateEditPropertiesSample';
import { FileEditMainSample } from './editors/FileEditMainSample';
import { FileEditPropertiesSample } from './editors/FileEditPropertiesSample';
import { ImageEditMainSample } from './editors/ImageEditMainSample';
import { ImageEditPropertiesSample } from './editors/ImageEditPropertiesSample';
import { LookupEditMainSample } from './editors/LookupEditMainSample';
import { LookupEditFilterSample } from './editors/LookupEditFilterSample';
import { LookupEditPropertiesSample } from './editors/LookupEditPropertiesSample';
import { MemoEditMainSample } from './editors/MemoEditMainSample';
import { MemoEditPropertiesSample } from './editors/MemoEditPropertiesSample';
import { RichEditMainSample } from './editors/RichEditMainSample';
import { RichEditPropertiesSample } from './editors/RichEditPropertiesSample';
import { MoneyEditMainSample } from './editors/MoneyEditMainSample';
import { MoneyEditPropertiesSample } from './editors/MoneyEditPropertiesSample';
import { MultiSelectChoiceEditMainSample } from './editors/MultiSelectChoiceEditMainSample';
import { MultiSelectChoiceEditPropertiesSample } from './editors/MultiSelectChoiceEditPropertiesSample';
import { NumberEditMainSample } from './editors/NumberEditMainSample';
import { NumberEditPropertiesSample } from './editors/NumberEditPropertiesSample';
import { TextEditMainSample } from './editors/TextEditMainSample';
import { TextEditMaskSample } from './editors/TextEditMaskSample';
import { TextEditPropertiesSample } from './editors/TextEditPropertiesSample';
import { LookupRecordContextMainSample } from './contexts/LookupRecordContextMainSample';
import { MainContextMainSample } from './contexts/MainContextMainSample';
import { RecordContextMainSample } from './contexts/RecordContextMainSample';
import { OperationButtonMainSample } from './common/OperationButtonMainSample';
import { OperationGridButtonSample } from './common/OperationGridButtonSample';
import { BarChartDataverseSample } from './charts/BarChartDataverseSample';
import { BarChartTimeOfDaySample } from './charts/BarChartTimeOfDaySample';
import { BarChartLinkedEntitySample } from './charts/BarChartLinkedEntitySample';
import { BarChartMainSample } from './charts/BarChartMainSample';
import { BarChartViewSample } from './charts/BarChartViewSample';
import { NumberRangesMainSample } from './charts/NumberRangesMainSample';
import { NumberRangesSeriesSample } from './charts/NumberRangesSeriesSample';
import { DoughnutChartMainSample } from './charts/DoughnutChartMainSample';
import { FunnelChartMainSample } from './charts/FunnelChartMainSample';
import { LineChartMainSample } from './charts/LineChartMainSample';
import { LineChartYoYSample } from './charts/LineChartYoYSample';
import { PieChartMainSample } from './charts/PieChartMainSample';
import { PolarAreaChartMainSample } from './charts/PolarAreaChartMainSample';
import { RadarChartMainSample } from './charts/RadarChartMainSample';
import { FileGridMainSample } from './grids/FileGridMainSample';
import { GridColumnsMainSample } from './grids/GridColumnsMainSample';
import { GridEditingMainSample } from './grids/GridEditingMainSample';
import { GridRelatedRecordsSample } from './grids/GridRelatedRecordsSample';
import { MainGridMainSample } from './grids/MainGridMainSample';
import { MainGridVirtualizeSample } from './grids/MainGridVirtualizeSample';
import { MainGridCardsSample } from './grids/MainGridCardsSample';
import { MainGridMapSample } from './grids/MainGridMapSample';
import { MainGridGroupingSample } from './grids/MainGridGroupingSample';
import { ManyToManyLookupEditorMainSample } from './grids/ManyToManyLookupEditorMainSample';
import { ManyToManyLookupEditorPropertiesSample } from './grids/ManyToManyLookupEditorPropertiesSample';
import { SubGridMainSample } from './grids/SubGridMainSample';
import { SubGridManyToManySample } from './grids/SubGridManyToManySample';
import { FetchXmlBuilderMainSample } from './utilities/FetchXmlBuilderMainSample';
import { DialogServiceMainSample } from './utilities/DialogServiceMainSample';
import { DialogServiceCustomSample } from './utilities/DialogServiceCustomSample';
import { FileViewerImageSample } from './viewers/FileViewerImageSample';
import { FileViewerMarkdownSample } from './viewers/FileViewerMarkdownSample';
import { FileViewerTextSample } from './viewers/FileViewerTextSample';
import { FileViewerZipSample } from './viewers/FileViewerZipSample';
import { FileViewerUnsupportedSample } from './viewers/FileViewerUnsupportedSample';
import { ImageViewerBareSample } from './viewers/ImageViewerBareSample';
import { ImageViewerCroppableSample } from './viewers/ImageViewerCroppableSample';
import { ImageViewerFramedSample } from './viewers/ImageViewerFramedSample';
import { ImageViewerToolbarSample } from './viewers/ImageViewerToolbarSample';
import { DisplayValuesAddressSample } from './viewers/DisplayValuesAddressSample';
import { DisplayValuesChoiceColorsSample } from './viewers/DisplayValuesChoiceColorsSample';
import { DisplayValuesStatusBadgeSample } from './viewers/DisplayValuesStatusBadgeSample';
import { TimelineMainSample } from './viewers/TimelineMainSample';
import { TimelineOptionsSample } from './viewers/TimelineOptionsSample';
import { CalendarMainSample } from './viewers/CalendarMainSample';
import { CalendarOptionsSample } from './viewers/CalendarOptionsSample';
import { CalendarMultiTableSample } from './viewers/CalendarMultiTableSample';
import { CalendarDayLayoutSample } from './viewers/CalendarDayLayoutSample';
import { CalendarWorkWeekLayoutSample } from './viewers/CalendarWorkWeekLayoutSample';
import { CalendarWeekLayoutSample } from './viewers/CalendarWeekLayoutSample';
import { CalendarMonthLayoutSample } from './viewers/CalendarMonthLayoutSample';
import { CalendarAgendaLayoutSample } from './viewers/CalendarAgendaLayoutSample';
import { CalendarTemplatesSample } from './viewers/CalendarTemplatesSample';
import { CalendarDetailsSample } from './viewers/CalendarDetailsSample';
import { KanbanBoardMainSample } from './viewers/KanbanBoardMainSample';
import { KanbanBoardCardsSample } from './viewers/KanbanBoardCardsSample';
import { MultiTableSearchMainSample } from './viewers/MultiTableSearchMainSample';
import { MultiTableSearchBoxSample } from './viewers/MultiTableSearchBoxSample';
import { TimelineTemplatesSample } from './viewers/TimelineTemplatesSample';
import { TimelineUpcomingSample } from './viewers/TimelineUpcomingSample';
import { TimelineAscendingSample } from './viewers/TimelineAscendingSample';
import { PageLayoutMainSample } from './layout/PageLayoutMainSample';
import { PageLayoutPropertiesSample } from './layout/PageLayoutPropertiesSample';
import { SectionColumnWidthsSample } from './layout/SectionColumnWidthsSample';
import { SectionLabelPositionSample } from './layout/SectionLabelPositionSample';
import { SummaryCardMainSample } from './layout/SummaryCardMainSample';
import { TabsMainSample } from './layout/TabsMainSample';
import { SectionMainSample } from './layout/SectionMainSample';
import { SidePanelMainSample } from './layout/SidePanelMainSample';
import { SectionPropertiesSample } from './layout/SectionPropertiesSample';
import { AccountDetailMainSample } from './demos/AccountDetailMainSample';
import { ACCOUNT_ID } from './demos/DetailPageRecords';
import { AccountMainSample } from './demos/AccountMainSample';
import { BarChartShowcaseMainSample } from './demos/BarChartShowcaseMainSample';
import { NumberRangesShowcaseMainSample } from './demos/NumberRangesShowcaseMainSample';
import { CardGroupingMainSample } from './demos/CardGroupingMainSample';
import { CardLayoutMainSample } from './demos/CardLayoutMainSample';
import { ContactMainSample } from './demos/ContactMainSample';
import { CustomCardRenderingMainSample } from './demos/CustomCardRenderingMainSample';
import { CustomCellRenderingMainSample } from './demos/CustomCellRenderingMainSample';
import { DashboardMainSample } from './demos/DashboardMainSample';
import { DoughnutChartShowcaseMainSample } from './demos/DoughnutChartShowcaseMainSample';
import { FileUploadMainSample } from './demos/FileUploadMainSample';
import { FileViewerShowcaseMainSample } from './demos/FileViewerShowcaseMainSample';
import { GridGroupingMainSample } from './demos/GridGroupingMainSample';
import { GridInfiniteScrollMainSample } from './demos/GridInfiniteScrollMainSample';
import { GridTotalsMainSample } from './demos/GridTotalsMainSample';
import { GridMapLayoutMainSample } from './demos/GridMapLayoutMainSample';
import { MapNearbyGridSample } from './demos/MapNearbyGridSample';
import { MapNearbyMainSample } from './demos/MapNearbyMainSample';
import { GridPinnedColumnsMainSample } from './demos/GridPinnedColumnsMainSample';
import { GridAdvancedFilteringMainSample } from './demos/GridAdvancedFilteringMainSample';
import { GridAdvancedFilteringRestrictedSample } from './demos/GridAdvancedFilteringRestrictedSample';
import { GridSavedViewsMainSample } from './demos/GridSavedViewsMainSample';
import { ImageEditorMainSample } from './demos/ImageEditorMainSample';
import { RichEditShowcaseMainSample } from './demos/RichEditShowcaseMainSample';
import { MapShowcaseMainSample } from './demos/MapShowcaseMainSample';
import { MultiTableSearchShowcaseMainSample } from './demos/MultiTableSearchShowcaseMainSample';
import { MultiTableSearchRecordMainSample } from './demos/MultiTableSearchRecordMainSample';
import { TimelineShowcaseMainSample } from './demos/TimelineShowcaseMainSample';
import { CalendarShowcaseMainSample } from './demos/CalendarShowcaseMainSample';
import { TimelineCustomRenderingMainSample } from './demos/TimelineCustomRenderingMainSample';
import { CalendarCustomRenderingMainSample } from './demos/CalendarCustomRenderingMainSample';
import { KanbanBoardShowcaseMainSample } from './demos/KanbanBoardShowcaseMainSample';
import { KanbanBoardTasksMainSample } from './demos/KanbanBoardTasksMainSample';
import { KanbanBoardPipelineMainSample } from './demos/KanbanBoardPipelineMainSample';
import { KanbanBoardDueDatesMainSample } from './demos/KanbanBoardDueDatesMainSample';
import { KanbanBoardCloseDatesMainSample } from './demos/KanbanBoardCloseDatesMainSample';
import { InlineEditingMainSample } from './demos/InlineEditingMainSample';
import { InlineRelatedDataMainSample } from './demos/InlineRelatedDataMainSample';
import { LineChartShowcaseMainSample } from './demos/LineChartShowcaseMainSample';
import { MyAccountMainSample } from './demos/MyAccountMainSample';
import { OpportunityDetailMainSample } from './demos/OpportunityDetailMainSample';
import { OpportunityMainSample } from './demos/OpportunityMainSample';
import { PieChartShowcaseMainSample } from './demos/PieChartShowcaseMainSample';
import { PolarAreaChartShowcaseMainSample } from './demos/PolarAreaChartShowcaseMainSample';
import { RadarChartShowcaseMainSample } from './demos/RadarChartShowcaseMainSample';

// `recordContext` is provided here (not in the sample files) so the source
// tab shows just the component being demonstrated. The wrapper supplies the
// surrounding `<RecordContext>` so the column-bound editors have somewhere
// to bind. Same table for all — ppp_democustomtable is a demo table with
// `ppp_name` (text) and `ppp_credithold` (bool) columns for parameter
// exploration.
defineSampleElement('ppp-sample-textedit-main', TextEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-textedit-properties', TextEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-textedit-mask', TextEditMaskSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-booledit-main', BoolEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-booledit-properties', BoolEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-choiceedit-main', ChoiceEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-choiceedit-properties', ChoiceEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-dateedit-main', DateEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-dateedit-properties', DateEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-fileedit-main', FileEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-fileedit-properties', FileEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-imageedit-main', ImageEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-imageedit-properties', ImageEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-lookupedit-main', LookupEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-lookupedit-properties', LookupEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-lookupedit-filter', LookupEditFilterSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-memoedit-main', MemoEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-memoedit-properties', MemoEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-richedit-main', RichEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-richedit-properties', RichEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-moneyedit-main', MoneyEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-moneyedit-properties', MoneyEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-multiselectchoiceedit-main', MultiSelectChoiceEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement(
  'ppp-sample-multiselectchoiceedit-properties',
  MultiSelectChoiceEditPropertiesSample,
  {
    recordContext: { table: 'ppp_democustomtable' },
  },
);
defineSampleElement('ppp-sample-numberedit-main', NumberEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-numberedit-properties', NumberEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});

// Layout samples — PageLayout and Section both use editors inside, so they
// still need a surrounding RecordContext envelope from the host.
defineSampleElement('ppp-sample-pagelayout-main', PageLayoutMainSample, {
  recordContext: { table: 'account' },
});
defineSampleElement('ppp-sample-pagelayout-properties', PageLayoutPropertiesSample, {
  recordContext: { table: 'account' },
});
defineSampleElement('ppp-sample-section-main', SectionMainSample, {
  recordContext: { table: 'account' },
});
defineSampleElement('ppp-sample-section-properties', SectionPropertiesSample, {
  recordContext: { table: 'account' },
});
defineSampleElement('ppp-sample-section-column-widths', SectionColumnWidthsSample, {
  recordContext: { table: 'account' },
});
defineSampleElement('ppp-sample-section-label-position', SectionLabelPositionSample, {
  recordContext: { table: 'account' },
});
defineSampleElement('ppp-sample-sidepanel-main', SidePanelMainSample);
defineSampleElement('ppp-sample-summary-card-main', SummaryCardMainSample);
defineSampleElement('ppp-sample-tabs-main', TabsMainSample);

// Context samples — the sample's job is to SHOW the context wrapper in
// the source, so each TSX renders its own MainContext / RecordContext /
// LookupRecordContext directly. We deliberately omit `recordContext`
// from the host envelope here so it doesn't double-wrap the sample with
// an outer RecordContext the source tab wouldn't reflect.
defineSampleElement('ppp-sample-maincontext-main', MainContextMainSample);
defineSampleElement('ppp-sample-recordcontext-main', RecordContextMainSample);
defineSampleElement('ppp-sample-operationbutton-main', OperationButtonMainSample);
defineSampleElement('ppp-sample-operationgridbutton-main', OperationGridButtonSample);
defineSampleElement(
  'ppp-sample-lookuprecordcontext-main',
  LookupRecordContextMainSample,
);

// Grid samples — same reasoning as context samples: the FileGrid sample
// owns its own `<MainContext>` for the Save / Refresh toolbar, so no
// outer recordContext envelope. (Future grid samples that drop into a
// pre-bound RecordContext can add it back via the options arg.)
defineSampleElement('ppp-sample-filegrid-main', FileGridMainSample);

// SubGrid samples live inside the parent record's `<RecordContext>` —
// same shape the Blazor demo page wraps everything in. Pin to the
// same account GUID so both stacks read the same related rows.
//
// `skipFetch: true` seeds the RecordContext with a stub `{ tableName, id }`
// record so the envelope doesn't fire a server fetch for the parent
// account. SubGrid only needs the parent reference (table + id) as a
// relationship filter key — it never reads the parent's column values —
// and a fetch failure on the parent would leave the sample area blank
// instead of rendering the related grid. Mirrors how the Blazor demo
// page passes a hand-constructed `new TableRecord { TableName, Id }`
// straight into `<RecordContext Record="..." />` for the same reason.
const SUBGRID_ACCOUNT_ID = 'dbbc9453-ee34-f111-88b4-6045bd02c77d';
defineSampleElement('ppp-sample-subgrid-main', SubGridMainSample, {
  recordContext: { table: 'account', id: SUBGRID_ACCOUNT_ID, skipFetch: true },
});
defineSampleElement('ppp-sample-subgrid-many-to-many', SubGridManyToManySample, {
  recordContext: { table: 'account', id: SUBGRID_ACCOUNT_ID, skipFetch: true },
});

// ManyToManyLookupEditor samples — also live inside an `<RecordContext>`
// (the Blazor demo page wraps in `account` create-mode). No id =
// create-mode record, same as Blazor's `new TableRecord { TableName = "account" }`.
defineSampleElement(
  'ppp-sample-manytomanylookupeditor-main',
  ManyToManyLookupEditorMainSample,
  { recordContext: { table: 'account' } },
);
defineSampleElement(
  'ppp-sample-manytomanylookupeditor-properties',
  ManyToManyLookupEditorPropertiesSample,
  { recordContext: { table: 'account' } },
);

// Chart samples — chart components don't need a RecordContext envelope
// (DataverseChart binds to its own DataSource; FluentUIChart takes
// inline labels/datasets).
defineSampleElement('ppp-sample-barchart-main', BarChartMainSample);
defineSampleElement('ppp-sample-barchart-dataverse', BarChartDataverseSample);
defineSampleElement('ppp-sample-barchart-time-of-day', BarChartTimeOfDaySample);
defineSampleElement('ppp-sample-barchart-view', BarChartViewSample);
defineSampleElement('ppp-sample-numberranges-main', NumberRangesMainSample);
defineSampleElement('ppp-sample-numberranges-series', NumberRangesSeriesSample);
defineSampleElement('ppp-sample-barchart-linked-entity', BarChartLinkedEntitySample);
defineSampleElement('ppp-sample-piechart-main', PieChartMainSample);
defineSampleElement('ppp-sample-doughnutchart-main', DoughnutChartMainSample);
defineSampleElement('ppp-sample-linechart-main', LineChartMainSample);
defineSampleElement('ppp-sample-linechart-yoy', LineChartYoYSample);
defineSampleElement('ppp-sample-funnelchart-main', FunnelChartMainSample);
defineSampleElement('ppp-sample-radarchart-main', RadarChartMainSample);
defineSampleElement('ppp-sample-polarareachart-main', PolarAreaChartMainSample);

// GridColumns sample renders a MainGrid against the `contact` table —
// no outer RecordContext envelope; the grid owns its own data fetching.
defineSampleElement('ppp-sample-gridcolumns-main', GridColumnsMainSample);
defineSampleElement('ppp-sample-gridediting-main', GridEditingMainSample);
defineSampleElement('ppp-sample-gridrelatedrecords', GridRelatedRecordsSample);

// MainGrid sample — standalone grid over the `contact` table, no outer
// RecordContext envelope (unlike SubGrid). The grid owns its own fetch.
defineSampleElement('ppp-sample-maingrid-main', MainGridMainSample);
// Infinite-scroll (`pagingMode={PagingMode.Virtualize}`) variant of the
// MainGrid demo. Same view shape, smaller chunk size so scrolling
// exercises the accumulator.
defineSampleElement('ppp-sample-maingrid-virtualize', MainGridVirtualizeSample);
defineSampleElement('ppp-sample-maingrid-cards', MainGridCardsSample);
defineSampleElement('ppp-sample-maingrid-map', MainGridMapSample);
defineSampleElement('ppp-sample-maingrid-grouping', MainGridGroupingSample);

// DialogService sample uses the `useDialogService` hook from the
// surrounding FluentDialogProvider (set up in define-sample-element).
// No record context needed; the hook is purely imperative.
defineSampleElement('ppp-sample-dialogservice-main', DialogServiceMainSample);
defineSampleElement('ppp-sample-dialogservice-custom', DialogServiceCustomSample);

// The FetchXML builder, wired the way a portal page wires it: the metadata hook
// and a `renderResults` that puts the rows in a MainGrid. No record context — the
// builder edits a query rather than a record.
defineSampleElement('ppp-sample-fetchxmlbuilder-main', FetchXmlBuilderMainSample);

// Viewer samples are pure presentational — no data dependencies. One ppp-sample-*
// element per Blazor sub-demo block on the FileViewer / ImageViewer pages so each
// `<DemoSection>` lights up its own live React example next to the Blazor one.
defineSampleElement('ppp-sample-fileviewer-image', FileViewerImageSample);
defineSampleElement('ppp-sample-fileviewer-markdown', FileViewerMarkdownSample);
defineSampleElement('ppp-sample-fileviewer-text', FileViewerTextSample);
defineSampleElement('ppp-sample-fileviewer-zip', FileViewerZipSample);
defineSampleElement('ppp-sample-fileviewer-unsupported', FileViewerUnsupportedSample);
defineSampleElement('ppp-sample-imageviewer-bare', ImageViewerBareSample);
defineSampleElement('ppp-sample-imageviewer-framed', ImageViewerFramedSample);
defineSampleElement('ppp-sample-imageviewer-toolbar', ImageViewerToolbarSample);
defineSampleElement('ppp-sample-imageviewer-croppable', ImageViewerCroppableSample);
// Display-value samples hand RecordContext an in-memory record, so they need no data either.
defineSampleElement('ppp-sample-displayvalues-statusbadge', DisplayValuesStatusBadgeSample);
// The featured account of the detail page demos, whose notes and messages are sample data. The
// timeline needs only the record's table and id, so nothing is fetched for it.
defineSampleElement('ppp-sample-timeline-main', TimelineMainSample, {
  recordContext: { table: 'account', id: ACCOUNT_ID, skipFetch: true },
});
defineSampleElement('ppp-sample-timeline-options', TimelineOptionsSample, {
  recordContext: { table: 'account', id: ACCOUNT_ID, skipFetch: true },
});
defineSampleElement('ppp-sample-timeline-templates', TimelineTemplatesSample, {
  recordContext: { table: 'account', id: ACCOUNT_ID, skipFetch: true },
});
defineSampleElement('ppp-sample-timeline-upcoming', TimelineUpcomingSample, {
  recordContext: { table: 'account', id: ACCOUNT_ID, skipFetch: true },
});
defineSampleElement('ppp-sample-timeline-ascending', TimelineAscendingSample, {
  recordContext: { table: 'account', id: ACCOUNT_ID, skipFetch: true },
});
// The calendar reads its appointments itself, a range at a time, so it needs no record context.
defineSampleElement('ppp-sample-calendar-main', CalendarMainSample);
defineSampleElement('ppp-sample-calendar-options', CalendarOptionsSample);
defineSampleElement('ppp-sample-calendar-multi-table', CalendarMultiTableSample);
defineSampleElement('ppp-sample-calendar-day-layout', CalendarDayLayoutSample);
defineSampleElement('ppp-sample-calendar-work-week-layout', CalendarWorkWeekLayoutSample);
defineSampleElement('ppp-sample-calendar-week-layout', CalendarWeekLayoutSample);
defineSampleElement('ppp-sample-calendar-month-layout', CalendarMonthLayoutSample);
defineSampleElement('ppp-sample-calendar-agenda-layout', CalendarAgendaLayoutSample);
defineSampleElement('ppp-sample-calendar-templates', CalendarTemplatesSample);
// MapLibre starts its worker from a URL. A library build has no URL of its own to give the worker
// (vite.config.samples.ts), so it travels as text in a chunk of its own, fetched when a map is
// first drawn, and starts from a blob.
setMapLibreWorkerUrl(() =>
  import('maplibre-gl/dist/maplibre-gl-worker.mjs?raw').then(({ default: source }) =>
    URL.createObjectURL(new Blob([source], { type: 'text/javascript' })),
  ),
);
defineSampleElement('ppp-sample-map-main', MapMainSample);
defineSampleElement('ppp-sample-map-records', MapRecordsSample);
defineSampleElement('ppp-sample-calendar-details', CalendarDetailsSample);
// The board reads its lanes itself, each on its own, so it needs no record context either.
defineSampleElement('ppp-sample-kanban-board-main', KanbanBoardMainSample);
defineSampleElement('ppp-sample-kanban-board-cards', KanbanBoardCardsSample);
defineSampleElement('ppp-sample-multi-table-search-main', MultiTableSearchMainSample);
defineSampleElement('ppp-sample-multi-table-search-box', MultiTableSearchBoxSample);
defineSampleElement('ppp-sample-displayvalues-address', DisplayValuesAddressSample);
defineSampleElement('ppp-sample-displayvalues-choicecolors', DisplayValuesChoiceColorsSample);

// The Demos pages: whole pages rather than one component each, so every sample owns what it
// needs — its own RecordContext (bound to the page's query parameter, as the Blazor page's is)
// and its own grids — and no envelope wrapper is configured. Their grids' navigate buttons open
// records on the same demo pages the Blazor grids do, where both stacks show the record again.
defineSampleElement('ppp-sample-demo-inlinerelateddata', InlineRelatedDataMainSample);
defineSampleElement('ppp-sample-demo-customcellrendering', CustomCellRenderingMainSample);
defineSampleElement('ppp-sample-demo-inlineediting', InlineEditingMainSample);
defineSampleElement('ppp-sample-demo-gridgrouping', GridGroupingMainSample);
defineSampleElement('ppp-sample-demo-gridtotals', GridTotalsMainSample);
defineSampleElement('ppp-sample-demo-gridmaplayout', GridMapLayoutMainSample);
defineSampleElement('ppp-sample-demo-mapnearby', MapNearbyMainSample);
defineSampleElement('ppp-sample-demo-mapnearbygrid', MapNearbyGridSample);
defineSampleElement('ppp-sample-demo-gridinfinitescroll', GridInfiniteScrollMainSample);
defineSampleElement('ppp-sample-demo-gridpinnedcolumns', GridPinnedColumnsMainSample);
defineSampleElement('ppp-sample-demo-gridadvancedfiltering', GridAdvancedFilteringMainSample);
defineSampleElement('ppp-sample-demo-gridadvancedfilteringrestricted', GridAdvancedFilteringRestrictedSample);
defineSampleElement('ppp-sample-demo-gridsavedviews', GridSavedViewsMainSample);
defineSampleElement('ppp-sample-demo-contact', ContactMainSample);
defineSampleElement('ppp-sample-demo-account', AccountMainSample);
defineSampleElement('ppp-sample-demo-accountdetail', AccountDetailMainSample);
defineSampleElement('ppp-sample-demo-opportunitydetail', OpportunityDetailMainSample);
defineSampleElement('ppp-sample-demo-opportunity', OpportunityMainSample);
defineSampleElement('ppp-sample-demo-cardlayout', CardLayoutMainSample);
defineSampleElement('ppp-sample-demo-cardgrouping', CardGroupingMainSample);
defineSampleElement('ppp-sample-demo-customcardrendering', CustomCardRenderingMainSample);
defineSampleElement('ppp-sample-demo-dashboard', DashboardMainSample);
defineSampleElement('ppp-sample-demo-barchart', BarChartShowcaseMainSample);
defineSampleElement('ppp-sample-demo-numberranges', NumberRangesShowcaseMainSample);
defineSampleElement('ppp-sample-demo-linechart', LineChartShowcaseMainSample);
defineSampleElement('ppp-sample-demo-piechart', PieChartShowcaseMainSample);
defineSampleElement('ppp-sample-demo-doughnutchart', DoughnutChartShowcaseMainSample);
defineSampleElement('ppp-sample-demo-radarchart', RadarChartShowcaseMainSample);
defineSampleElement('ppp-sample-demo-polarareachart', PolarAreaChartShowcaseMainSample);
defineSampleElement('ppp-sample-demo-myaccount', MyAccountMainSample);
defineSampleElement('ppp-sample-demo-fileupload', FileUploadMainSample);
defineSampleElement('ppp-sample-demo-fileviewer', FileViewerShowcaseMainSample);
defineSampleElement('ppp-sample-demo-imageeditor', ImageEditorMainSample);
defineSampleElement('ppp-sample-demo-richedit', RichEditShowcaseMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-demo-timeline', TimelineShowcaseMainSample);
defineSampleElement('ppp-sample-demo-calendar', CalendarShowcaseMainSample);
defineSampleElement('ppp-sample-demo-timelinecustomrendering', TimelineCustomRenderingMainSample);
defineSampleElement('ppp-sample-demo-calendarcustomrendering', CalendarCustomRenderingMainSample);
defineSampleElement('ppp-sample-demo-map', MapShowcaseMainSample);
defineSampleElement('ppp-sample-demo-multitablesearch', MultiTableSearchShowcaseMainSample);
defineSampleElement('ppp-sample-demo-multitablesearchrecord', MultiTableSearchRecordMainSample);
defineSampleElement('ppp-sample-demo-kanbanboard', KanbanBoardShowcaseMainSample);
defineSampleElement('ppp-sample-demo-kanbanboardtasks', KanbanBoardTasksMainSample);
defineSampleElement('ppp-sample-demo-kanbanboardpipeline', KanbanBoardPipelineMainSample);
defineSampleElement('ppp-sample-demo-kanbanboardduedates', KanbanBoardDueDatesMainSample);
defineSampleElement('ppp-sample-demo-kanbanboardclosedates', KanbanBoardCloseDatesMainSample);
